<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Registration status</h1>
    <p class="a2p-sub">
      Your registration is with the carriers. We check for updates automatically about
      once a minute — you can leave this page or close it and come back anytime. Clean
      submissions typically approve in 2–4 business days.
    </p>

    <!-- Actual errors only -->
    <A2PCallout v-if="error && isActualError" type="danger">
      <strong>Error:</strong> {{ error }}
    </A2PCallout>

    <!-- Approved / waiting / submitted / failed banners -->
    <A2PCallout v-if="isApproved" type="success">
      <strong>Registration approved!</strong> Twilio has approved your A2P 10DLC registration.
      You can now proceed to purchase a phone number.
    </A2PCallout>
    <A2PCallout v-else-if="isWaitingForApproval" type="warn">
      <strong>Waiting for Twilio approval.</strong> Typically 1–7 business days. We'll
      update this page automatically when the carriers respond — no need to sit here.
    </A2PCallout>

    <!-- Main status card -->
    <div v-if="registration && progress" class="a2p-status-card">
      <div class="a2p-status-head">
        <div>
          <div class="a2p-status-step" :class="getStatusClass(status)">
            <span class="ico">{{ getStatusEmoji(status) }}</span>
            {{ progress.currentStep }}
          </div>
          <div class="a2p-status-desc">{{ progress.currentStepDescription }}</div>
        </div>
        <div class="a2p-status-badge" :class="getStatusClass(status)">
          {{ getStatusDisplayText(status) }}
        </div>
      </div>

      <!-- Progress bar -->
      <div class="a2p-status-progress">
        <div class="label">
          <span>Overall progress</span>
          <span>{{ progress.progressPercentage }}%</span>
        </div>
        <div class="bar">
          <div
            class="fill"
            :class="status === 'approved' ? 'ok' : (status === 'rejected' || status === 'failed' ? 'err' : '')"
            :style="{ width: progress.progressPercentage + '%' }"
          ></div>
        </div>
        <div class="sub">{{ progress.completedSteps }} of {{ progress.totalSteps }} steps completed</div>
      </div>

      <!-- Last update -->
      <div v-if="lastStatusUpdate" class="a2p-status-footer">
        Last updated: {{ formatDate(lastStatusUpdate) }}
        <span v-if="latestStatusDetails"> · {{ latestStatusDetails }}</span>
      </div>

      <div class="a2p-status-refresh">
        <button type="button" class="a2p-btn a2p-btn-secondary" @click="refreshStatus" :disabled="refreshing">
          {{ refreshing ? 'Refreshing…' : 'Refresh status' }}
        </button>
      </div>
    </div>

    <!-- Step-by-step -->
    <div v-if="progress && progress.steps" class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Carrier pipeline</div>
        <h2>Step-by-step progress</h2>
      </div>
      <div class="a2p-steps">
        <div
          v-for="(step, index) in progress.steps"
          :key="step.key"
          class="a2p-step-row"
          :class="{ done: isStepDone(step, progress), current: isCurrentStep(step, index) || isParallelBundleReview(step, progress) }"
        >
          <div class="icon">
            <span v-if="isStepDone(step, progress)">✓</span>
            <span v-else-if="isCurrentStep(step, index) || isParallelBundleReview(step, progress)" class="spinner"></span>
            <span v-else>·</span>
          </div>
          <div class="body">
            <div class="name">{{ step.name }}</div>
            <div v-if="isCurrentStep(step, index)" class="sub">In progress…</div>
            <div v-else-if="isParallelBundleReview(step, progress)" class="sub">Still in review. Your brand registration has already been submitted alongside it.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status history -->
    <div v-if="statusHistory && statusHistory.length" class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Webhook log · {{ statusHistory.length }}</div>
        <h2>Status history</h2>
      </div>
      <div class="a2p-history">
        <div v-for="(h, i) in statusHistory" :key="i" class="a2p-history-row">
          <div class="timestamp">{{ formatDate(h.created_at) }}</div>
          <div class="body">
            <div>
              <span class="a2p-status-badge inline" :class="getStatusClass(h.new_status)">
                {{ getStatusDisplayText(h.new_status) }}
              </span>
              <span v-if="h.webhook_type" class="via">via {{ formatWebhookType(h.webhook_type) }}</span>
            </div>
            <div v-if="h.status_details" class="details">{{ h.status_details }}</div>
            <div v-if="h.error_message" class="error">⚠ {{ h.error_message }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Registration details -->
    <div v-if="registration" class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Filed with the carriers</div>
        <h2>Registration details</h2>
      </div>
      <div class="a2p-review-section">
        <div class="a2p-kv"><div class="k">Business name</div><div class="v">{{ registration.BusinessName || registration.business_name || '—' }}</div></div>
        <div class="a2p-kv"><div class="k">Tax ID / EIN</div><div class="v">{{ registration.EIN || registration.ein || '—' }}</div></div>
        <div class="a2p-kv"><div class="k">Business type</div><div class="v">{{ registration.BusinessType || registration.business_type || '—' }}</div></div>
        <div class="a2p-kv"><div class="k">Representative</div><div class="v">{{ registration.RepName || registration.rep_name || '—' }}</div></div>
        <div class="a2p-kv"><div class="k">Contact email</div><div class="v">{{ registration.RepEmail || registration.rep_email || '—' }}</div></div>
        <div class="a2p-kv"><div class="k">Registration ID</div><div class="v mono">{{ registration.ID || registration.id }}</div></div>
      </div>

      <div v-if="isSuperAdmin && hasAnyTwilioSID" style="margin-top: 12px">
        <button type="button" class="a2p-link-btn" @click="showTwilioDetails = !showTwilioDetails">
          {{ showTwilioDetails ? '− Hide' : '+ Show' }} technical details
        </button>
        <div v-if="showTwilioDetails" class="a2p-review-section" style="margin-top: 12px">
          <div v-if="registration.TwilioAccountSID" class="a2p-kv"><div class="k">Twilio subaccount SID</div><div class="v mono">{{ registration.TwilioAccountSID }}</div></div>
          <div v-if="registration.SecondaryCustomerProfileSID || registration.business_profile_sid" class="a2p-kv"><div class="k">Customer profile SID</div><div class="v mono">{{ registration.SecondaryCustomerProfileSID || registration.business_profile_sid }}</div></div>
          <div v-if="registration.TrustProductSID" class="a2p-kv"><div class="k">Trust product SID</div><div class="v mono">{{ registration.TrustProductSID }}</div></div>
          <div v-if="registration.BusinessEndUserSID" class="a2p-kv"><div class="k">Business end-user SID</div><div class="v mono">{{ registration.BusinessEndUserSID }}</div></div>
          <div v-if="registration.RepEndUserSID" class="a2p-kv"><div class="k">Rep end-user SID</div><div class="v mono">{{ registration.RepEndUserSID }}</div></div>
          <div v-if="registration.AddressResourceSID" class="a2p-kv"><div class="k">Address SID</div><div class="v mono">{{ registration.AddressResourceSID }}</div></div>
          <div v-if="registration.BrandRegistrationSID || registration.brand_registration_sid" class="a2p-kv"><div class="k">Brand registration SID</div><div class="v mono">{{ registration.BrandRegistrationSID || registration.brand_registration_sid }}</div></div>
          <div v-if="registration.CampaignSID || registration.campaign_sid" class="a2p-kv"><div class="k">Campaign SID</div><div class="v mono">{{ registration.CampaignSID || registration.campaign_sid }}</div></div>
          <div v-if="registration.MessagingServiceSID || registration.messaging_service_sid" class="a2p-kv"><div class="k">Messaging service SID</div><div class="v mono">{{ registration.MessagingServiceSID || registration.messaging_service_sid }}</div></div>
          <div class="a2p-kv"><div class="k">Site ID</div><div class="v mono">{{ registration.SiteID || registration.site_id }}</div></div>
        </div>
      </div>
    </div>

    <!-- Start over for failed registrations -->
    <A2PCallout v-if="canStartOver" type="warn">
      <strong>Registration failed.</strong> You can delete it and start fresh.
      <div style="margin-top: 10px">
        <button type="button" class="a2p-btn a2p-btn-secondary" @click="handleStartOver" :disabled="startingOver">
          {{ startingOver ? 'Deleting…' : 'Start over' }}
        </button>
      </div>
    </A2PCallout>

    <div class="a2p-actions">
      <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('back')">
        Back to form
      </button>
      <div style="display: flex; gap: 10px; flex-wrap: wrap">
        <button
          v-if="canProceedToPhoneNumbers"
          type="button"
          class="a2p-btn a2p-btn-primary"
          @click="$emit('next')"
        >
          Continue to phone numbers →
        </button>
        <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('close')">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import A2PCallout from './components/A2PCallout.vue';

// Terminal states: the carriers have made a final call, so we stop polling.
const TERMINAL_STATUSES = ['approved', 'rejected', 'failed'];

// Pure, side-effect-free — mirrored in cypress/node-tests/a2p-status-polling.test.js.
// KEEP IN SYNC with that mirror.
export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.includes(String(status || '').toLowerCase());
}

// Decide whether a single 60s poll tick should fire a refresh.
// Skip when the tab is hidden (no wasted network on a backgrounded tab) and
// once the status is terminal. Non-terminal + visible (incl. an empty/unknown
// status still settling) → poll.
export function shouldPollTick(status, hidden) {
  if (hidden) return false;
  return !isTerminalStatus(status);
}

// Who may see the raw Twilio SIDs in the technical-details panel: Super Admins
// (Teamio internal) only. Dental-practice staff — Admin / Manager / Employee —
// never do. Pure, side-effect-free — mirrored in
// cypress/node-tests/a2p-status-polling.test.js. KEEP IN SYNC with that mirror.
export function canSeeTechnicalDetails(role) {
  return role === 'Super Admin';
}

// Which step row is "current" (2026-09-14). The Brand is now submitted while the
// A2P bundle is still in review — Twilio does not require the bundle approval
// first — so "Brand Registration" can be done while "Customer Profile Approval"
// is still open. Follow the backend's currentStep (the headline above), but
// never point back at the bundle-approval step once the Brand exists, even if an
// older backend still names it. That still-open bundle row is shown as in review
// alongside the current step instead.
// Executed (not mirrored) by cypress/node-tests/a2p-wizard-current-step.test.js,
// which extracts everything between these markers — keep them pure.
// A2P-STEP-PICKER:BEGIN
function stepsOf(progress) {
  return (progress && Array.isArray(progress.steps)) ? progress.steps : [];
}

function keyCompleted(progress, key) {
  const s = stepsOf(progress).find((x) => x.key === key);
  return !!(s && s.completed);
}

export function brandSubmitted(progress) {
  return keyCompleted(progress, 'brand_registration');
}

// An approved Brand or campaign means the bundle review is effectively done,
// even when its approval webhook was never recorded (older backends leave that
// step incomplete forever on such sites). Never show a finished practice as
// "still in review".
export function isStepDone(step, progress) {
  if (!step) return false;
  if (step.completed) return true;
  return step.key === 'customer_profile_approval'
    && (keyCompleted(progress, 'brand_approval') || keyCompleted(progress, 'campaign_approval'));
}

export function pickCurrentStepIndex(progress) {
  const steps = stepsOf(progress);
  if (!steps.length) return -1;
  const skip = (s) => s.key === 'customer_profile_approval' && brandSubmitted(progress);
  const open = (s) => !isStepDone(s, progress) && !skip(s);
  const byBackend = steps.findIndex((s) => s.name === progress.currentStep && open(s));
  if (byBackend >= 0) return byBackend;
  return steps.findIndex((s) => open(s) && s.required !== false);
}

export function isParallelBundleReview(step, progress) {
  if (!step) return false;
  return step.key === 'customer_profile_approval' && !isStepDone(step, progress) && brandSubmitted(progress);
}
// A2P-STEP-PICKER:END

export default {
  name: 'A2PWizardStatus',
  components: { A2PCallout },
  props: {
    eyebrow: String,
    status: String,
    registration: Object,
    error: String,
    statusHistory: Array,
    progress: Object,
    timeline: Object,
    lastStatusUpdate: String,
    latestStatusDetails: String,
  },
  data() {
    return { refreshing: false, showTwilioDetails: false, startingOver: false, pollTimer: null };
  },
  mounted() {
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    status(newStatus) {
      // Once the registration reaches a terminal state, stop polling immediately —
      // no point re-fetching a finished registration.
      if (isTerminalStatus(newStatus)) this.stopPolling();
    },
  },
  computed: {
    ...mapState('customer', ['user']),
    isSuperAdmin() {
      return canSeeTechnicalDetails(this.user && this.user.Role);
    },
    hasTwilioResources() {
      return this.registration && (
        this.registration.SecondaryCustomerProfileSID || this.registration.business_profile_sid ||
        this.registration.BrandRegistrationSID || this.registration.brand_registration_sid ||
        this.registration.CampaignSID || this.registration.campaign_sid ||
        this.registration.MessagingServiceSID || this.registration.messaging_service_sid
      );
    },
    hasAnyTwilioSID() {
      if (!this.registration) return false;
      return !!(
        this.registration.TwilioAccountSID ||
        this.registration.SecondaryCustomerProfileSID || this.registration.business_profile_sid ||
        this.registration.TrustProductSID ||
        this.registration.BusinessEndUserSID ||
        this.registration.RepEndUserSID ||
        this.registration.AddressResourceSID ||
        this.registration.BrandRegistrationSID || this.registration.brand_registration_sid ||
        this.registration.CampaignSID || this.registration.campaign_sid ||
        this.registration.MessagingServiceSID || this.registration.messaging_service_sid ||
        this.registration.SiteID || this.registration.site_id
      );
    },
    canStartOver() {
      if (!this.registration) return false;
      const s = (this.registration.Status || this.registration.status || '').toLowerCase();
      return ['failed', 'rejected'].includes(s) && !this.hasTwilioResources;
    },
    isActualError() {
      if (!this.error) return false;
      const lower = this.error.toLowerCase();
      const success = ['successfully', 'retrieved', 'completed', 'success'];
      return !success.some((x) => lower.includes(x));
    },
    isWaitingForApproval() {
      const waiting = ['submitted', 'pending', 'in-review', 'pending-review', 'in_progress'];
      const s = (this.registration?.Status || this.registration?.status || '').toLowerCase();
      return waiting.includes(s);
    },
    isApproved() {
      const approved = ['approved', 'twilio-approved', 'twilio-verified'];
      const s = (this.registration?.Status || this.registration?.status || '').toLowerCase();
      return approved.includes(s);
    },
    canProceedToPhoneNumbers() {
      const approved = ['approved', 'twilio-approved', 'twilio-verified'];
      const s = this.registration?.Status || this.registration?.status;
      return this.registration && s && approved.includes(s.toLowerCase());
    },
  },
  methods: {
    // Expose the pure helpers as methods so the template/tests can reach the same
    // logic; both delegate to the module-level functions above.
    shouldPollTick(status, hidden) {
      return shouldPollTick(status, hidden);
    },
    startPolling() {
      if (this.pollTimer) return;
      if (isTerminalStatus(this.status)) return; // already finished — nothing to poll
      this.pollTimer = setInterval(() => this.pollTick(), 60000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    pollTick() {
      // Stop entirely if we've reached a terminal state between ticks.
      if (isTerminalStatus(this.status)) {
        this.stopPolling();
        return;
      }
      const hidden = typeof document !== 'undefined' && document.hidden;
      if (!shouldPollTick(this.status, hidden)) return; // skip this tick (tab hidden)
      this.$emit('refresh');
    },
    formatDate(d) {
      if (!d) return 'N/A';
      return moment(d).format('MMM DD, YYYY h:mm A');
    },
    async handleStartOver() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This will delete the current failed registration and allow you to start fresh. Continue?',
        { title: 'Start over?', okVariant: 'warning', okTitle: 'Yes, start over', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) return;
      this.startingOver = true;
      try { this.$emit('start-over'); } finally { this.startingOver = false; }
    },
    async refreshStatus() {
      this.refreshing = true;
      try { this.$emit('refresh'); } finally {
        setTimeout(() => { this.refreshing = false; }, 1000);
      }
    },
    getStatusClass(s) {
      return ({
        approved: 'ok', rejected: 'err', failed: 'err',
        pending: 'warn', in_progress: 'info',
        submitted: 'info', draft: 'muted',
      })[s] || 'muted';
    },
    getStatusEmoji(s) {
      return ({
        approved: '✓', rejected: '✗', failed: '⚠',
        pending: '⏳', in_progress: '⚙',
        submitted: '📤', draft: '✎',
      })[s] || '•';
    },
    getStatusDisplayText(s) {
      return ({
        approved: 'Approved', rejected: 'Rejected', failed: 'Failed',
        pending: 'Under review', in_progress: 'Processing',
        submitted: 'Submitted', draft: 'Draft',
      })[s] || (s ? String(s) : 'Unknown');
    },
    isCurrentStep(step, index) {
      return index === pickCurrentStepIndex(this.progress);
    },
    isParallelBundleReview(step, progress) {
      return isParallelBundleReview(step, progress);
    },
    isStepDone(step, progress) {
      return isStepDone(step, progress);
    },
    formatWebhookType(w) {
      return String(w).replace(/[-_.]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
.a2p-status-card {
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
  padding: 22px;
  margin: 16px 0 28px;
}
.a2p-status-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}
.a2p-status-step {
  font-size: 18px;
  font-weight: 600;
  color: var(--a2p-fg);
  display: flex;
  align-items: center;
  gap: 10px;
}
.a2p-status-step .ico { font-size: 20px; }
.a2p-status-step.ok { color: var(--a2p-success-ink); }
.a2p-status-step.err { color: var(--a2p-danger-ink); }
.a2p-status-step.warn { color: var(--a2p-warn-ink); }
.a2p-status-step.info { color: var(--a2p-accent-ink); }
.a2p-status-desc {
  color: var(--a2p-fg-2);
  font-size: 14px;
  margin-top: 4px;
}
.a2p-status-badge {
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: var(--a2p-surface-2);
  color: var(--a2p-fg-2);
}
.a2p-status-badge.ok   { background: var(--a2p-success-soft); color: var(--a2p-success-ink); }
.a2p-status-badge.err  { background: var(--a2p-danger-soft);  color: var(--a2p-danger-ink); }
.a2p-status-badge.warn { background: var(--a2p-warn-soft);    color: var(--a2p-warn-ink); }
.a2p-status-badge.info { background: var(--a2p-accent-soft);  color: var(--a2p-accent-ink); }
.a2p-status-badge.inline { font-size: 11px; padding: 2px 8px; }

.a2p-status-progress .label {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 6px;
  color: var(--a2p-fg-2);
}
.a2p-status-progress .bar {
  height: 10px;
  background: var(--a2p-surface-3);
  border-radius: 99px;
  overflow: hidden;
}
.a2p-status-progress .fill {
  height: 100%;
  background: var(--a2p-accent);
  border-radius: 99px;
  transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}
.a2p-status-progress .fill.ok { background: var(--a2p-success); }
.a2p-status-progress .fill.err { background: var(--a2p-danger); }
.a2p-status-progress .sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--a2p-fg-3);
}

.a2p-status-footer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--a2p-border);
  font-size: 12px;
  color: var(--a2p-fg-3);
}
.a2p-status-refresh {
  margin-top: 14px;
  text-align: center;
}

.a2p-review-section {
  padding: 16px 20px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
  margin: 12px 0;
}
.a2p-kv .v.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  word-break: break-all;
}

.a2p-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.a2p-step-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-s);
}
.a2p-step-row.done { background: var(--a2p-success-soft); border-color: var(--a2p-success); }
.a2p-step-row.current { background: var(--a2p-accent-soft); border-color: var(--a2p-accent); }
.a2p-step-row .icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--a2p-surface-3);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  color: var(--a2p-fg-2);
  flex-shrink: 0;
}
.a2p-step-row.done .icon { background: var(--a2p-success); color: #fff; }
.a2p-step-row.current .icon { background: var(--a2p-accent); color: #fff; }
.a2p-step-row .name {
  font-weight: 500;
  font-size: 14px;
  color: var(--a2p-fg);
}
.a2p-step-row .sub { font-size: 12px; color: var(--a2p-fg-2); margin-top: 2px; }
.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: a2p-st-spin 0.8s linear infinite;
}
@keyframes a2p-st-spin { to { transform: rotate(360deg); } }

.a2p-history {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.a2p-history-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 14px;
  padding: 12px 14px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-s);
}
.a2p-history-row .timestamp {
  color: var(--a2p-fg-3);
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.a2p-history-row .via { color: var(--a2p-fg-3); font-size: 12px; margin-left: 8px; }
.a2p-history-row .details { color: var(--a2p-fg-2); font-size: 13px; margin-top: 4px; }
.a2p-history-row .error { color: var(--a2p-danger-ink); font-size: 13px; margin-top: 4px; }
@media (max-width: 600px) {
  .a2p-history-row { grid-template-columns: 1fr; }
}

.a2p-link-btn {
  background: transparent;
  border: none;
  color: var(--a2p-accent);
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
}
.a2p-link-btn:hover { text-decoration: underline; }
</style>
